import { ds } from "js/core/models/dataService";
import { $, _, Backbone, SVG } from "js/vendor";
import log from "js/core/logger";
import { app } from "js/namespaces";
import * as geom from "js/core/utilities/geom";
import { slideTemplates, SlidePlaceholderTemplate } from "js/core/slide_templates/slide_template";
import { appVersion } from "../appVersion";
import { SlideCanvasLayouter } from "js/core/slide_templates/slideCanvasLayouter";
import { CanvasEventType, TrayType, BackgroundStyleType, ElementTextBlockPositionType, SlideLockType } from "common/constants";
import Profiler from "js/core/profiler";
import { AnimationManager } from "js/elements/animation/animationManager";
import { audioContext, getAudioBufferFromUrl } from "js/core/utilities/audioUtilities";
import isConnected from "./utilities/isConnected";
import { MigrationsHelper } from "./utilities/migrationsHelper";
import { FeatureType } from "./models/features";
import { trackActivity } from "./utilities/utilities";
import { getStaticUrl } from "js/config";
import "css/canvas.scss";
import "css/controls.scss";
import { BaseElement, DecorativeElement } from "js/elements/base/BaseElement";
import { Convert } from "js/core/utilities/geom";
import { ShowMessageDialog } from "../react/components/Dialogs/BaseDialog";
import { getLibraryModelFromSlide } from "../../common/dataServiceHelpers";
import { CanvasElement } from "../elements/base/CanvasElement";

const isPuppeteer = navigator.userAgent.includes("HeadlessChrome/");

const profiler = Profiler.create({
    name: "Canvas",
    type: Profiler.AVG_LOAD_TIME,
    warnTime: 1500
});

let SlideCanvas = Backbone.View.extend({
    selectedElement: null,
    isSelectable: true,
    isRendered: false,
    className: "slide_canvas",

    initialize: function(options) {
        this.options = options;
        this.isNew = options.isNew || false;

        this.$el.attr("id", options.dataModel.id);

        this.dataModel = options.dataModel;
        this.slide = this.dataModel;

        this.CANVAS_WIDTH = this.options.canvasWidth || 1280;

        this.CANVAS_HEIGHT = this.options.canvasHeight || 720;
        this.isPlayback = options.isPlayback;
        this.isEditable = false;

        this.listenTo(this.dataModel, "change:modifiedAt", () => {
            if (!isConnected.connected) return;
            this.updateThumbnailURL().catch(err => log.warn(err.message));
        });

        this.listenTo(this.dataModel, "destroy", () => {
            this.cancelRender();
        });

        this.listenTo(this.dataModel, "updateDataState", (model, options) => {
            const prevTemplateId = this.model ? this.model.template_id : null;
            this.model = model.dataState;

            if (options.needsMigration) {
                this.migrateModel(options);
            }

            if (this.isRendered && !this._updating) {
                //refresh the canvas!
                if (this.model.template_id !== prevTemplateId) {
                    this.updateTemplate(this.dataModel.dataState.template_id);
                }
                this.refreshCanvasAutoRevert();
            }
        });

        this.listenTo(this.dataModel, "change:libraryItemId", (model, options) => {
            if (model.has("libraryItemId")) {
                if (ds.teams && ds.teams.length) {
                    const libraryItem = getLibraryModelFromSlide(this.dataModel);
                    if (libraryItem) {
                        this.listenTo(libraryItem, "change:isDisabled", (model, value) => {
                            if (value) {
                                this.lockSlide(null, SlideLockType.ORG);
                            } else if (app.user.features.isFeatureEnabled(FeatureType.EDIT_LIBRARY_ITEMS, this.dataModel.presentation.getWorkspaceId())) {
                                this.unlockSlide(SlideLockType.ORG);
                            }
                        });
                    }
                }
            }
        });

        this.throttledRefreshCanvas = _.throttle(() => this.refreshCanvas(), 16);

        this.raiseRefreshCanvasEvent = _.throttle(() => {
            this.trigger(CanvasEventType.REFRESH);
        }, 100, {
            leading: false,
            trailing: true
        });

        this.cancelSpinnerId = null;
        this.invalidThumbnail = false;

        this.isAnimating = false;
    },

    showBranding: function() {
        if (window.isPlayer) {
            return ds.selection && ds.selection.presentation && ds.selection.presentation.showBranding;
        } else {
            if (!ds.selection.presentation) {
                // note: ds.selection.presentation does not exist when opening shared theme editor from presentation library
                return false;
            } else {
                const workspaceId = ds.selection.presentation.getWorkspaceId();
                return !app.user.features.isFeatureEnabled(FeatureType.REMOVE_BAI_BRANDING, workspaceId);
            }
        }
    },

    getSlideIndex() {
        // Slide index is passed explicitly as a paremeter when running in the renderer
        if (window.isRenderer) {
            return this.options.slideIndex || 0;
        }

        //ds.selection.presentation will not exist for canvas shown in shared team theme when opened from presentation library
        if (!ds.selection.presentation) {
            return 0;
        }

        const playableSlideModels = ds.selection.presentation.getPlayableSlideModels();
        let slideIndex = 0;
        for (slideIndex; slideIndex < playableSlideModels.length; slideIndex++) {
            if (playableSlideModels[slideIndex].get("id") !== this.dataModel.id) {
                continue;
            }
            break;
        }
        return slideIndex;
    },

    cancelRender: function() {
        this.renderSlidePromise = null;
        this.isReady = false;
        this.isRendered = false;
        this.hideSpinner();
    },

    updateTemplate: function(templateId) {
        this.$el.removeClass("slide_placeholder_canvas");

        if (this.layouter) {
            this.layouter.destroy();
            this.layouter = null;
        }

        // // find slide template for the slide associated with this canvas
        var template = _.find(slideTemplates, { id: templateId });
        if (template) {
            this.slideTemplate = new template();
        } else {
            log.error("INVALID TEMPLATE ID: " + templateId);
            this.slideTemplate = new SlidePlaceholderTemplate();
        }

        // check template version
        if (this.slideTemplate.migrate) {
            try {
                this.slideTemplate.migrate(this.dataModel.get("template_version") || 0, this.dataModel);
            } catch (ex) {
                log.error("Failed to migrate template", ex);
            }
        }

        // create a new BaseCanvasLayouter for the slide
        this.layouter = this.getLayouter();
    },

    getLayouter: function() {
        return new SlideCanvasLayouter(this, this.slideTemplate);
    },

    getTheme: function() {
        return app.currentTheme;
    },

    getBackgroundColor: function() {
        if (this.model.layout.trayLayout == TrayType.BACKGROUND) {
            return BackgroundStyleType.IMAGE;
        } else {
            return this.layouter.canvasElement.background.canvasBackgroundColor;
        }
    },

    getSlideColor: function() {
        return this.model.layout.slideColor || app.currentTheme.get("defaultSlideColor") || "theme";
    },

    getScale: function() {
        return this.canvasScale;
    },

    getMaxScale: function() {
        return this.maxCanvasScale || this.canvasScale;
    },

    getElementByUniquePath: function(uniquePath) {
        if (uniquePath.startsWith("/")) {
            uniquePath = uniquePath.substr(1);
        }
        let ids = uniquePath.split("/");

        let element = this.layouter.canvasElement.elements[ids[0]];
        for (let i = 1; i < ids.length; i++) {
            element = element.getChild(ids[i]);
        }
        return element;
    },

    //-----------------------------------------------------------------------------------------------------------------------
    // Render and Layout
    //-----------------------------------------------------------------------------------------------------------------------
    render: function() {
        this.$el.empty();
        this.$el.css({
            width: this.CANVAS_WIDTH,
            height: this.CANVAS_HEIGHT
        });
        this.renderSlidePromise = null;

        this.rootSVG = SVG(this.el);
        this.rootSVG.attr("id", "CanvasSVG").width(this.CANVAS_WIDTH).height(this.CANVAS_HEIGHT);
        this.svg = this.rootSVG.group().attr("id", "root");
        this.$svg = $(this.svg.node);

        // this.elements = [];

        if (!this.isPlayback) {
            this.$clickShield = this.$el.addEl($.div("slide_click_shield"));
        }

        this.$thumbnail = $.img("", "thumbnail");
        // Uncomment to see if thumbnails are rendering
        // this.$thumbnail.css("outline", "5px solid red");
        this.$el.addEl(this.$thumbnail);

        if (!this.isPlayback) {
            if (ds.selection.slideLock.locked) {
                this.lockSlide(ds.selection.slideLock.user);
            }
        }

        if (!this.isNew) {
            this.$svg.hide();
            this.$el.spinner(true);
        }

        return this;
    },

    _migrate_5() {
        // these templates had defaultTrayLayout in slide_template but never saved in model
        if (this.model.template_id === "bullets_image" && !this.model.layout.trayLayout) {
            this.model.layout.trayLayout = "left_inline_half";
        }
        if (this.model.template_id === "long_bio" && !this.model.layout.trayLayout) {
            this.model.layout.trayLayout = "left_tray_half";
        }
        if (this.model.template_id === "title" && this.model.layout.trayLayout) {
            this.model.oldTrayLayout = {
                layout: this.model.layout.trayLayout,
                tray: _.clone(this.model.elements.tray),
            };
            this.model.layout.trayLayout = TrayType.NONE;
        }
        if ((this.model.template_id === "contactus" || this.model.template_id === "business_card")) {
            this.model.layout.trayLayout = this.model.layout.trayLayout || "left_half_tray";
            this.model.oldTrayLayout = {
                layout: this.model.layout.trayLayout,
                tray: _.clone(this.model.elements.tray),
            };
            this.model.layout.trayLayout = TrayType.NONE;
            this.model.elements.tray = null;
        }

        // migrate trays
        switch (this.model.layout.trayLayout) {
            case "bottom_tray":
            case "top_tray":
            case "top_tray_half":
                // top and bottom tray are deprecated
                this.model.layout.trayLayout = TrayType.NONE;
                break;
            // half trays migrate to tray with trayWidth set to half canvas_width
            case "left_tray_half":
                this.model.layout.trayLayout = TrayType.LEFT_TRAY;
                this.model.elements.tray && (this.model.elements.tray.trayWidth = this.CANVAS_WIDTH / 2);
                break;
            case "right_tray_half":
                this.model.layout.trayLayout = TrayType.RIGHT_TRAY;
                this.model.elements.tray && (this.model.elements.tray.trayWidth = this.CANVAS_WIDTH / 2);
                break;
            case "right_inline_half":
                this.model.layout.trayLayout = TrayType.RIGHT_INLINE;
                this.model.elements.tray && (this.model.elements.tray.trayWidth = 590);
                break;
            case "left_inline_half":
                this.model.layout.trayLayout = TrayType.LEFT_INLINE;
                this.model.elements.tray && (this.model.elements.tray.trayWidth = 590);
                break;
            // other trays need to have their trayWidth set to their preset widths from old version
            case TrayType.LEFT_TRAY:
            case TrayType.RIGHT_TRAY:
                this.model.elements.tray && (this.model.elements.tray.trayWidth = 333);
                break;
            case TrayType.LEFT_INLINE:
            case TrayType.RIGHT_INLINE:
                this.model.elements.tray && (this.model.elements.tray.trayWidth = 377);
                break;
        }

        // show gutter should be off for all trays
        this.model.elements.tray && (this.model.elements.tray.showGutter = false);

        // migrate header
        if (this.model.layout.showHeader == true) {
            this.model.layout.headerPosition = "top";
        } else {
            if (this.model.layout.headerPosition != "top" || this.model.layout.showHeader != undefined) {
                this.model.layout.headerPosition = "none";
            }
        }

        // migrate old elementTextBlockPosition
        if (this.model.layout.elementTextBlockPosition == "above") {
            this.model.layout.elementTextBlockPosition = ElementTextBlockPositionType.NONE;
        }
        if (this.model.layout.elementTextBlockPosition == "below") {
            this.model.layout.elementTextBlockPosition = ElementTextBlockPositionType.INLINE;
        }
        if (this.model.layout.showElementTitle) {
            this.model.layout.showElementTitle = null;
        }
    },

    migrateModel({ save } = {}) {
        if (this.model.version >= appVersion) {
            return;
        }

        if (this.isMigrating) {
            return;
        }

        this.isMigrating = true;
        try {
            const lowerBoundInclusiveVersion = this.model.version || 4;
            const upperBoundInclusiveVersion = appVersion;
            console.groupCollapsed(`[${this.dataModel.id}] migrating slide model`);

            MigrationsHelper.migrate(this, lowerBoundInclusiveVersion, upperBoundInclusiveVersion);

            let canvasElement = new CanvasElement({ id: "root", canvas: this });
            canvasElement.template = this.slideTemplate;
            canvasElement.build(this.model, 0, true, lowerBoundInclusiveVersion, upperBoundInclusiveVersion);

            this.model.version = upperBoundInclusiveVersion;

            console.log(this.model);
            if (save) {
                this.saveCanvasModel({ undo: false, isFromMigration: true });
            }
            console.log(`Migration is saved: ${save}`);

            console.groupEnd();
        } finally {
            this.isMigrating = false;
        }
    },

    renderModel: function() {
        this.model = this.dataModel.dataState;
        this.updateTemplate(this.model.template_id);

        const saveMigrations = !(window.isPlayer || window.isRenderer || this.isPlayback);
        this.migrateModel({ save: saveMigrations });

        this.$svg.show();
        this.isRendered = true;

        return this.refreshCanvasAutoRevert({ forceRender: true });
    },

    updateCanvasModel: async function(transition = false, forceRender = false) {
        // TODO this causes UI to pop on transition or flash
        // if (transition) {
        //     app.mainView.editorView.selectionLayer.$el.hide();
        // }
        return this.refreshCanvasAutoRevert({ transition, forceRender }).then(() => {
            return this.saveCanvasModel();
        }).finally(() => {
            // if (transition) {
            //     app.mainView.editorView.selectionLayer.$el.show();
            // }
        });
    },

    saveCanvasModel: async function(options) {
        //make sure dataState is the model.
        this.dataModel.dataState = this.model;
        return this.dataModel.commit(options); //create a fresh model to ensure things are in sync.
    },

    revertCanvasModel: async function({
        transition = false,
        forceRender = false,
        lowerBoundInclusiveVersion,
        upperBoundInclusiveVersion,
        doMigration
    } = {}) {
        if (this.model.template_id !== this.dataModel.get("template_id")) {
            this.updateTemplate(this.dataModel.get("template_id"));
        }
        this.dataModel.discardChanges();
        this.model = this.dataModel.dataState;
        return this.layouter.generate(
            this.model,
            {
                transition,
                forceRender,
                lowerBoundInclusiveVersion,
                upperBoundInclusiveVersion,
                doMigration
            },
            new geom.Size(this.CANVAS_WIDTH, this.CANVAS_HEIGHT)
        ).then(() => {
            // if (!options.suppressRefreshCanvasEvent) {
            // this.raiseRefreshCanvasEvent();
            this.trigger(CanvasEventType.REFRESH);
            // }
        });
    },

    refreshCanvasAutoRevert: function({
        transition = false,
        forceRender = false,
        lowerBoundInclusiveVersion,
        upperBoundInclusiveVersion,
        doMigration
    } = {}) {
        return this.refreshCanvas({
            transition,
            forceRender,
            lowerBoundInclusiveVersion,
            upperBoundInclusiveVersion,
            doMigration
        }).catch(err => {
            log.warn(err);
            this.revertCanvasModel();
            return Promise.reject(err);
        });
    },

    refreshCanvas: function({
        transition = false,
        forceRender = false,
        lowerBoundInclusiveVersion,
        upperBoundInclusiveVersion,
        suppressRefreshCanvasEvent,
        doMigration
    } = {}) {
        if (this.model.template_id !== this.dataModel.get("template_id")) {
            this.updateTemplate(this.model.template_id, this.dataModel.get("template_id"));
        }

        return this.layouter.generate(
            this.model,
            {
                transition,
                forceRender,
                lowerBoundInclusiveVersion,
                upperBoundInclusiveVersion,
                suppressRefreshCanvasEvent,
                doMigration
            },
            new geom.Size(this.CANVAS_WIDTH, this.CANVAS_HEIGHT)
        ).then(() => {
            if (!suppressRefreshCanvasEvent) {
                if (this.layouter.isGenerating) {
                    console.warn("Attempting to refresh canvas while another generate is still in progress. This can cause exception because calculatedProps = null!");
                }
                this.trigger(CanvasEventType.REFRESH);
            }

            // beautiful.ai branding
            return this.renderBranding();
        });
    },

    refreshElement(element, transition, suppressRefreshCanvasEvent = false) {
        this.layouter.renderElement(element, transition);
        if (!suppressRefreshCanvasEvent) {
            this.trigger(CanvasEventType.REFRESH);
        }
    },

    refreshElements(elements, transition, suppressRefreshCanvasEvent = false) {
        this.layouter.renderElements(elements, transition);
        if (!suppressRefreshCanvasEvent) {
            this.trigger(CanvasEventType.REFRESH);
        }
    },

    quickRefreshCanvas: function() {
        return this.layouter.render(this.layouter.layout);
        // return this.layouter.refreshLayout(new geom.Size(this.CANVAS_WIDTH, this.CANVAS_HEIGHT));
    },

    renderCanvas: function() {
        return this.layouter.render(this.layouter.layout);
    },

    handleGenerateComplete: function() {
        this.trigger(CanvasEventType.REFRESH);
    },

    renderBranding: async function() {
        if (this.showBranding()) {
            if (!this.$watermark) {
                this.$watermark = this.$el.addEl($.div(""));
            }
            let watermarkStyle = "watermark";
            let backgroundColor = this.getBackgroundColor().name;

            if (backgroundColor !== "background_light" && backgroundColor !== "background_accent") {
                watermarkStyle += " reverse";
            }
            if (!this.layouter.showFooter) {
                watermarkStyle += " no-footer";
            }

            this.$watermark.css("background", this.getBackgroundColor().toHexString());

            if (this.$watermark[0].className != watermarkStyle) {
                this.$watermark[0].className = watermarkStyle;

                const image = new Image();
                this.$watermark.html(image);

                await new Promise((resolve, reject) => {
                    image.onload = () => {
                        resolve();
                    };
                    image.onerror = () => {
                        reject(new Error("Could not load branding image"));
                    };

                    if (watermarkStyle.contains("reverse")) {
                        image.src = getStaticUrl(`/images/beautifulai-logos/watermark-reverse.svg`);
                    } else {
                        image.src = getStaticUrl(`/images/beautifulai-logos/watermark.svg`);
                    }
                });
            }
        } else {
            if (this.$watermark) {
                this.$watermark.remove();
                this.$watermark = null;
            }
        }
    },

    showSpinner: function(delay) {
        clearTimeout(this.cancelSpinnerId);
        if (delay) {
            this.cancelSpinnerId = setTimeout(() => {
                this.$el.spinner(true);
            }, delay);
        } else {
            this.$el.spinner(true);
        }
    },

    hideSpinner: function() {
        clearTimeout(this.cancelSpinnerId);
        this.$el.spinner(false);
    },

    renderSlide: function(fade) {
        if (this.detached) {
            return Promise.resolve(false);
        }

        // if renderSlidePromise exists, we have already triggered the render slide, so don't trigger it again.
        if (!this.renderSlidePromise) {
            const profilerId = profiler.start("renderSlide");
            this.isReady = false;
            this.isRendered = false;

            if (this.isNew) {
                // We treat creating a new slide differently so it loads and appears faster in the app.
                // This essentially short circuits the rendering by avoiding the load and the ready check.
                this.isNew = false;
                const promise = this.renderModel().then(() => {
                    // at any time the renderSlide can be cancelled and restarted, this check makes sure that we are still
                    // processing the same promise.
                    if (promise !== this.renderSlidePromise) {
                        return;
                    }
                    this.$thumbnail.hide();
                    this.hideSpinner();
                    this.isReady = true;
                    profiler.end(profilerId);
                    return true;
                });
                this.renderSlidePromise = promise;
            } else {
                log.info(log.CANVAS)("loading slide " + this.dataModel.id);
                const promise = this.dataModel.load()
                    .then(() => {
                        if (promise !== this.renderSlidePromise) {
                            return;
                        }
                        return this.renderModel();
                    }).then(() => {
                        // Cancelled render check
                        if (promise !== this.renderSlidePromise) {
                            return;
                        }
                        // this.layout();
                        return this.resetPlayback(false);
                    })
                    .then(() => {
                        this.isReady = true;
                        //Fade if we want a transition between a thumbnail to the svg.
                        if (fade) {
                            this.$thumbnail.velocity("transition.fadeOut", {
                                delay: 50,
                                duration: 300,
                                display: "none"
                            });
                        } else {
                            this.$thumbnail.hide();
                        }
                        this.hideSpinner();
                        profiler.end(profilerId);
                        return true;
                    });

                this.renderSlidePromise = promise;
            }
        }

        return this.renderSlidePromise;
    },

    // Only set fallback if the slide must show something.
    renderThumbnail: function() {
        if (this.detached) {
            return;
        }

        this.$thumbnail.velocity("stop"); // in case the animation from showing a thumbnail hasn't completed
        this.$thumbnail.css("opacity", 1);
        this.$thumbnail.show();

        this.emptyCanvas();
    },

    renderLoading: function(showSpinner) {
        if (this.isReady || this.detached) {
            return;
        }

        this.$thumbnail.show();

        if (showSpinner) {
            this.showSpinner();
        }
    },

    updateThumbnailURL: function() {
        if (this.options.noThumbnail) return Promise.resolve();

        return this.dataModel.getThumbnailUrl(ds.selection.presentation.id)
            .then(url => {
                if (this.$thumbnail) {
                    return new Promise((resolve, reject) => {
                        this.$thumbnail.off().on("load", () => {
                            this.$thumbnail.addClass("loaded");
                            resolve();
                        }).on("error", () => {
                            reject(new Error("Invalid url " + url));
                        }).attr("src", url);
                        this.$el.spinner(false);
                    });
                }
            });
    },

    //-----------------------------------------------------------------------------------------------------------------------
    // Mouse handler
    //-----------------------------------------------------------------------------------------------------------------------
    onClickCanvas: function(event) {
        if (!this.isSelectable) return;
        if (!this.options.editable) return;
        if (event.originalEvent.hasSelectedElement) return;

        event.stopPropagation();
        if (ds.selection.element) {
            this.clearSelection();
        }
    },

    onMouseEnter: function() {
        // ds.selection.rolloverElement = this.layouter.elements.primary;
    },

    show: function() {
        this.isVisible = true;
        this.$el.css("opacity", 1);
    },

    hide: function() {
        this.isVisible = false;
        this.$el.css("opacity", 0);
    },

    setAsCurrentCanvas: function() {
        this.isCurrentCanvas = true;

        this.$el.addClass("current_slide");
        const lock = ds.selection.slideLock;
        if (lock.locked) {
            this.lockSlide(lock.user);
        } else {
            this.unlockSlide(SlideLockType.COLLABORATION);
            this.unlockSlide(SlideLockType.ORG);
        }
        this.listenTo(ds.selection, "change:slideLock", () => {
            const lock = ds.selection.slideLock;
            if (lock.locked) {
                this.lockSlide(lock.user);
            } else {
                this.unlockSlide(SlideLockType.COLLABORATION);
            }
        });

        if (this.$clickShield) {
            this.$clickShield.hide();
        }
    },

    removeAsCurrentCanvas: function() {
        this.isCurrentCanvas = false;

        this.stopElements();

        this.stopListening(ds.selection, "change:slideLock");
        this.hideLock();
        this.clearSelection();
        this.$el.removeClass("current_slide");
        if (this.dataModel.hasChanges && !this.dataModel.destroyed) {
            const props = {
                "slide_id": this.dataModel.get("id"),
                "slides_created": 0,
            };
            trackActivity("Slide", "ContentModified", null, null, props);
            this.dataModel.finishedEditing();
        }

        this.isEditable = false;

        if (this.$clickShield) {
            this.$clickShield.show();
        }
    },

    //-----------------------------------------------------------------------------------------------------------------------
    // Editable and Locking
    //-----------------------------------------------------------------------------------------------------------------------

    lockSlide: function(user, type = "collaboration") {
        log.debug(log.COLLABORATION)("lock slide " + this.dataModel.id);
        this.setEditable(false, type);
        this.clearSelection();
        if (user) {
            this.showLock(user);
        }
    },

    unlockSlide: function(type) {
        log.debug(log.COLLABORATION)("unlock slide" + this.dataModel.id);
        this.setEditable(true, type);
        this.hideLock();
    },

    setEditable: function(editable, type) {
        this.isEditable = editable;
        log.debug(log.COLLABORATION)("set editable to " + this.isEditable + " for slide " + this.dataModel.id);
        app.mainView.editorView.trigger(`${type}:lockChanged`, editable, this);
    },

    showLock: function(user) {
        app.mainView.currentMenu.showCollaborationLock(user);
    },

    hideLock: function() {
        if (app.mainView.currentMenu) {
            app.mainView.currentMenu.hideCollaborationLock();
        }
    },

    //-----------------------------------------------------------------------------------------------------------------------
    // Playback
    //-----------------------------------------------------------------------------------------------------------------------

    prepareToEdit: function() {
        this.setEditable(true);
    },

    getAnimationManager: function(options) {
        let animationManager = new AnimationManager(this);
        return animationManager;
    },

    registerAnimations: function(animationManager) {
        this.layouter.primary && this.layouter.primary.registerAnimations(animationManager);
        this.layouter.annotations && this.layouter.annotations.registerAnimations(animationManager);
    },

    currentPlaybackStage: 0,
    playbackStages: [],

    resetPlayback: async function(animate = true) {
        if (this.playbackStages.length) {
            await this.playbackStages[0].callback(this.playbackStages[0], animate);
            this.currentPlaybackStage = 0;
        }
    },

    stopPlayback: function() {

    },

    clearPlaybackStages: function() {
        this.playbackStages = [];
    },

    addPlaybackStage: function(stage) {
        this.playbackStages.push(stage);
    },

    isPlaybackComplete: function() {
        return this.currentPlaybackStage >= this.playbackStages.length - 1;
    },

    goToStage: async function(index, animate = true) {
        if (this.currentPlaybackStage != index && index < this.playbackStages.length) {
            this.currentPlaybackStage = index;
            let playbackStage = this.playbackStages[this.currentPlaybackStage];
            return playbackStage.callback(playbackStage, animate);
        }
    },

    playNextStage: async function(animate = true) {
        if (this.currentPlaybackStage < this.playbackStages.length - 1) {
            this.currentPlaybackStage += 1;
            let playbackStage = this.playbackStages[this.currentPlaybackStage];
            return playbackStage.callback(playbackStage, animate);
        }
    },

    playPrevStage: async function(animate = true) {
        if (this.currentPlaybackStage > 0) {
            this.currentPlaybackStage -= 1;
            let playbackStage = this.playbackStages[this.currentPlaybackStage];
            return playbackStage.callback(playbackStage, animate);
        }
    },

    prepareToShowElements: function() {
        if (this.layouter) {
            _.each(this.layouter.elements, element => {
                element.prepareToShowElement();
            });

            this.renderBranding()
                .catch(err => log.error(err));
        }
    },

    stopElements: function() {
        if (this.layouter) {
            _.each(this.layouter.elements, element => {
                element.stopElement();
            });
        }
    },

    hasAudio: function() {
        return this.dataModel && this.dataModel.has("audioAsset");
    },

    setupAudio: function() {
        if (this.hasAudio()) {
            console.log("setupaudio", this.dataModel.get("audioAsset"));
            return ds.assets.getAssetById(this.dataModel.get("audioAsset"), "audio")
                .then(asset => asset.getBaseUrl())
                .then(assetUrl => getAudioBufferFromUrl(assetUrl))
                .then(audioBuffer => {
                    this.audioBuffer = audioBuffer;
                })
                .catch(err => {
                    this.isPlayingAudio = false;
                    throw new Error(err);
                });
        } else {
            return Promise.resolve();
        }
    },

    playAudio: function(showDialog = false, onBeforePlay = null) {
        if (this.hasAudio()) {
            const play = () => new Promise((resolve, reject) => {
                if (!this.audioBuffer) {
                    throw new Error("No loaded audioBuffer");
                }
                this.isPlayingAudio = true;
                this.audio = audioContext.createBufferSource();
                this.audio.buffer = this.audioBuffer;
                this.audio.connect(audioContext.destination);
                this.audio.start(0);

                this.audio.onended = () => {
                    let interruptedPlay = !this.isPlayingAudio;
                    this.isPlayingAudio = false;
                    this.audioBuffer = null;
                    resolve(interruptedPlay);
                };
                this.audio.onerror = () => {
                    reject(new Error("Audio stream failed"));
                };
            });

            return this.setupAudio()
                .then(() => {
                    if (showDialog) {
                        return ShowMessageDialog({
                            title: "Click to play slide",
                            buttonText: "Play"
                        });
                    }
                })
                .then(() => {
                    if (onBeforePlay) {
                        onBeforePlay();
                    }
                    return play();
                });
        } else {
            return Promise.resolve();
        }
    },

    resumeAudio: function() {
        this.isPlayingAudio = true;
        audioContext.resume();
    },

    stopAudio: function() {
        this.isPlayingAudio = false;
        if (this.audio) {
            this.audio.stop();
            this.audio = null;
            this.audioBuffer = null;
        }
    },

    //-----------------------------------------------------------------------------------------------------------------------
    // Elements
    //-----------------------------------------------------------------------------------------------------------------------

    getPrimaryElement: function() {
        return this.layouter.canvasElement.elements.primary;
    },

    getTrayElement: function() {
        return this.layouter.canvasElement.elements.tray;
    },

    // addElement: function (element) {
    //     this.elements.push(element);
    //     this.trigger(CanvasEventType.ELEMENT_ADDED, element);
    //
    //     element.render();
    //
    //     this.listenTo(element, ElementEventType.LOADED, element => this.onElementLoaded(element));
    //
    //     if (this.isEditable) {
    //         element._enableEditing();
    //     }
    // },
    //
    // removeElement: function (element) {
    //     this.trigger(CanvasEventType.ELEMENT_REMOVED, element);
    //     this.elements.remove(element);
    //     element.remove();
    // },
    //
    // removeAllElements: function () {
    //     while (this.elements.length) {
    //         this.removeElement(this.elements[0]);
    //     }
    // },

    // loaded: function () {
    //     return new Promise(resolve => {
    //         if (this.areElementsLoaded()) {
    //             resolve();
    //         } else {
    //             this.listenTo(this, "all_elements_loaded", () => {
    //                 resolve();
    //             });
    //         }
    //     });
    // },
    //
    // onElementLoaded: function () {
    //     //         console.log("Canvas::Element loaded " + element.toString());
    //     if (this.areElementsLoaded()) {
    //         //             console.log("Canvas::canvas for " + this.model.id + " all elements loaded");
    //         this.trigger("all_elements_loaded");
    //     }
    // },

    // registerChildElement: function () {
    //     this.trigger('update_spellcheck');
    // },
    //
    // unregisterChildElement: function () {
    //     this.trigger('update_spellcheck');
    // },

    // areElementsLoaded: function () {
    //     return !_.find(this.elements, {
    //         isLoaded: false
    //     });
    // },
    //
    // debugLoad: function () {
    //     _.each(this.elements, element => {
    //         element.debugLoad();
    //     });
    // },

    clearSelection: function() {
        if (ds) {
            ds.selection.element = null;
            ds.selection.rolloverElement = null;
        }
    },

    //---------------------------------------------------------------------------------------------------------------------
    // Find stuff at the mouse point
    //---------------------------------------------------------------------------------------------------------------------
    findFirstElementAtPoint(x, y, type) {
        let elementsAtPoint = [];

        if (!this.layouter) return null;

        this.findElementsAtPoint(elementsAtPoint, this.layouter, x, y, type);

        let foundElements = [];
        for (let element of elementsAtPoint) {
            switch (type) {
                case "selectable":
                    if (element.canSelect) foundElements.push(element);
                    break;
                case "rollover":
                    if (element.canRollover) foundElements.push(element);
                    break;
                case "dropImage":
                    if (element.canDropImage) foundElements.push(element);
                    break;
                case "doubleClickable":
                    if (element.doubleClickToSelect) foundElements.push(element);
                    break;
                case "interactive":
                    if (element.isInteractive) foundElements.push(element);
                    break;
                default:
                    // anything else check for element type
                    if (element.type == type) foundElements.push(element);
            }
        }

        // console.log(elementsAtPoint, foundElements);
        if (foundElements.length) {
            return foundElements[0];
        } else {
            return null;
        }
    },

    findElementsAtPoint(elementsAtPoint, node, x, y, type) {
        _.each(_.sortBy(node.elements, el => el.calculatedProps ? el.calculatedProps.layer : -99).reverse(), element => {
            if (element instanceof BaseElement) {
                if (element.canSelectChildElements) {
                    this.findElementsAtPoint(elementsAtPoint, element, x, y, type);
                }

                let elementPt = Convert.ScreenToCanvasCoordinates(this, x, y);
                if (element.calculatedProps && element.containsPoint(elementPt)) {
                    elementsAtPoint.push(element);
                }
            }
        });
    },

    waitForImages() {
        return this.layouter.canvasElement.imagesLoadPromise;
    },

    emptyCanvas: function() {
        this.stopElements();

        this.$svg.empty();
        this.$svg.hide();

        if (this.layouter) {
            this.layouter.destroy();
            this.layouter = null;
        }
        this.cancelRender();
        this.isRendered = false;
        this.isReady = false;
    },

    remove: function() {
        this.emptyCanvas();
        this.renderSlidePromise = null;
        this.detached = true;
        this.$thumbnail && this.$thumbnail.remove();
        this.$thumbnail = null;
        this.isReady = false;
        this.audioBuffer = null;
        Backbone.View.prototype.remove.apply(this, arguments);
    },

});

class CanvasRenderer {
    /**
     * The canvas renderer manages rendering a group of canvases and will prerender canvases along with set optimization
     * flags as necessary. You must set the canvases to manage by calling setCanvases and passing in an array of canvases.
     *
     * Prerendering works by rendering alternating between next slides and previous slides ideally having an even amount
     * of previous and next slides renderered. Optionally you can add a delay to add breathing space between prerenders.
     *
     * @param {Object} options - CanvasRenderer options.
     * @param {number} options.prerender - The amount of slides to prerender and keep in memory.
     * @param {boolean} options.thumbnail - Show the thumbnail while loading.
     * @param {boolean} options.fastLoad - Set the fastLoad flag when rendering elements. Currently used to load a
     *        low res image for the first slide. The renderer should not use fastLoad.
     * @param {number} options.delay - Delay in ms to add between prerenders.
     * @param {boolean} options.showLoader - Show a loader while the slide is loading and rendering.
     * @param {boolean} options.showDelayedLoader - Show a loader if a certain amount of time has passed. Useful when showing the thumbnail while loading.
     * @param {number} options.delayedLoaderTime - the time in ms to delay showing the loader if showDelayedLoader is set to true.
     */
    constructor(options) {
        options = Object.assign({
            prerender: 20,
            thumbnail: false,
            fastLoad: true,
            delay: 200,
            showLoader: true,
            showDelayedLoader: false,
            delayedLoaderTime: 500,
        }, options);
        this.canvases = [];
        this.prerenderIndices = [];
        this.thumbnail = options.thumbnail;
        this.prerender = options.prerender;
        this.fastLoad = options.fastLoad;
        this.delay = options.delay;
        this.delayedLoaderTime = options.delayedLoaderTime;
        this.showDelayedLoader = options.showDelayedLoader;
        this.prerenderId = null;
        this.showLoader = options.showLoader;
        this.prerenderId = 0;
        this.currentCanvasId = null;
        this.currentRenderPromise = null;
    }

    /**
     * Sets the canvases to manage.
     * @param canvases
     */
    setCanvases(canvases) {
        this.canvases.forEach(canvas => {
            if (canvases.indexOf(canvas) === -1) {
                canvas.cancelRender();
            }
        });
        this.prerenderIndices = [];
        this.canvases = canvases.slice();
        this.currentCanvasId = null;
        this.currentRenderPromise = null;
    }

    renderThumbnails() {
        this.prerenderId++;
        this.prerenderIndices = [];
        this.currentCanvasId = null;
        this.currentRenderPromise = null;
        for (let i = 0; i < this.canvases.length; i++) {
            const canvas = this.canvases[i];
            if (canvas.detached) {
                continue;
            }
            canvas.cancelRender(); //cancel any pending render
            canvas.renderThumbnail();
            canvas.updateThumbnailURL().catch(() => {
                canvas.renderSlide();
            });
        }
    }

    /**
     * Call this to render a canvas at an index. The CanvasRenderer will handle how to render the slide and if it needs
     * to prerender more slides.
     * @param index
     * @returns {Promise} The same results as calling canvas.renderSlide() for the canvas at the index.
     */
    renderSlide(index = 0, forceFastLoad = false) {
        // Guard to protect against calling this method multiple times on the same canvas.
        const canvas = this.canvases[index];
        if (!canvas) {
            return Promise.resolve(false);
        }
        if (this.currentCanvasId === canvas.cid) {
            return this.currentRenderPromise;
        }
        this.currentCanvasId = canvas.cid;

        //reset our list of indices to load.
        this.prerenderIndices = [];
        let fade = false;
        this.prerenderId++;

        this.currentRenderPromise = this._renderCanvas(index, fade, this.fastLoad || forceFastLoad).then(ready => {
            this.fastLoad = false;
            return ready;
        });

        // Alternate between 1,-1,2,-2 ... from the start index.
        for (let i = 1; i < this.canvases.length * 2; i++) {
            const offset = (i % 2 === 0) ? Math.floor(-i / 2) : Math.ceil(i / 2);
            const targetIndex = index + offset;
            if (targetIndex >= 0 && targetIndex < this.canvases.length) {
                this.prerenderIndices.push(targetIndex);
            }
        }
        //truncate by the prerender amount.
        this.prerenderIndices = this.prerenderIndices.slice(0, this.prerender);

        if (this.thumbnail) {
            const skipPreview = { [index]: true };
            this.prerenderIndices.forEach(i => {
                skipPreview[i] = true;
            });
            for (let i = 0; i < this.canvases.length; i++) {
                const canvas = this.canvases[i];
                if (!skipPreview[i]) {
                    canvas.renderThumbnail();
                } else {
                    canvas.renderLoading(this.showLoader);
                }
            }
        }
        //prerender asyncronously. Do not return this promise as it will resolve when all slides have been prerendered.
        this.currentRenderPromise.then(() => {
            return this._prerenderCanvases(fade);
        });

        // We only return the promise for the current slide, vs the promise triggering the prerenders. The promise result is the
        // same result as calling canvas.renderSlide().
        return this.currentRenderPromise;
    }

    /**
     * Simple wrapper to render the canvas and show a spinner as needed.
     *
     * @param index
     * @param fade
     * @returns {Promise} The same results as calling canvas.renderSlide() for the canvas at the index.
     * @private
     */
    _renderCanvas(index, fade, fastLoad = false) {
        const canvas = this.canvases[index];
        if (!canvas) {
            return Promise.resolve(false);
        } else {
            canvas.fastLoad = fastLoad;
            if (this.showDelayedLoader && !canvas.isReady) {
                canvas.showSpinner(this.delayedLoaderTime);
            }
            return canvas.renderSlide(fade);
        }
    }

    /**
     * Render a canvas but with a timeout delay specified by the options.
     *
     * @param index
     * @param fade
     * @returns {Promise} The same results as calling canvas.renderSlide() for the canvas at the index.
     * @private
     */
    _delayRenderCanvas(index, fade) {
        return new Promise(resolve => {
            // create an id so we can see if we are still in the same state when the timeout resolves.
            const id = this.prerenderId;
            setTimeout(() => {
                resolve(id);
            }, this.delay);
        }).then(id => {
            // Are will still in the same state? if not, exit.
            if (id !== this.prerenderId) {
                return false;
            }
            return this._renderCanvas(index, fade);
        });
    }

    /**
     * Iterate through the slides that should be prerendered and sequentially render them until they've all
     * been rendered.
     *
     * The way this works is that it will dequeue the index to render, render, then try again. Since this is async, the
     * prerenderIndices can change, and this will continue to behave correctly.
     *
     * @param fade
     * @returns {Promise} A promise that resolves when all canvases have been prerendered.
     * @private
     */
    _prerenderCanvases(fade) {
        if (this.prerenderIndices.length === 0) {
            return Promise.resolve();
        }
        return this._delayRenderCanvas(this.prerenderIndices.shift(), fade).then(() => {
            return this._prerenderCanvases();
        });
    }

    /**
     * Cancel any rendering that is pending or in progress.
     */
    cancelAll() {
        this.prerenderId++;
        this.prerenderIndices = [];
        this.currentCanvasId = null;
        this.currentRenderPromise = null;
        for (let i = 0; i < this.canvases.length; i++) {
            this.canvases[i].cancelRender();
        }
    }

    /**git
     * Removes all canvases that the CanvasRenderer is managing.
     */
    removeAll() {
        this.currentCanvasId = null;
        this.currentRenderPromise = null;
        for (let i = 0; i < this.canvases.length; i++) {
            this.canvases[i].remove();
        }
    }
}

export { CanvasEventType, SlideCanvas, CanvasRenderer };

